import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { articleService } from '../../services/articleService';

interface Article {
  _id: string;
  title: string;
  date: string;
  category: 'immigration-updates' | 'student-visa' | 'work-visa' | 'family-visa';
  description: string;
  status: 'draft' | 'published';
  featuredImage?: {
    url: string;
    public_id: string;
  };
  sections: Array<{
    title: string;
    icon: string;
    details: Array<{ text: string }>;
    image?: {
      url: string;
      public_id: string;
    };
  }>;
  author?: string;
  createdAt: string;
  updatedAt: string;
}

const ArticleList: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const data = await articleService.getArticles();
      setArticles(data);
      setError('');
    } catch (err) {
      setError('Failed to fetch articles');
      console.error('Error fetching articles:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (articleId: string) => {
    navigate(`/admin/edit-article/${articleId}`);
  };

  const handleDelete = async () => {
    if (!selectedArticle) return;

    try {
      await articleService.deleteArticle(selectedArticle._id);
      setArticles(articles.filter(article => article._id !== selectedArticle._id));
      setDeleteDialogOpen(false);
      setSelectedArticle(null);
    } catch (err) {
      setError('Failed to delete article');
      console.error('Error deleting article:', err);
    }
  };

  const getCategoryColor = (category: Article['category']) => {
    const colors = {
      'immigration-updates': theme.palette.info.main,
      'student-visa': theme.palette.success.main,
      'work-visa': theme.palette.warning.main,
      'family-visa': theme.palette.error.main,
    };
    return colors[category] || theme.palette.primary.main;
  };

  if (loading) {
    return (
      <Box sx={{ py: 4 }}>
        <Container maxWidth="lg">
          <Typography>Loading articles...</Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Manage Articles
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/create-article')}
          >
            Create Article
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {articles.map((article) => (
                <TableRow key={article._id}>
                  <TableCell>{article.title}</TableCell>
                  <TableCell>
                    <Chip
                      label={article.category}
                      size="small"
                      sx={{
                        bgcolor: `${getCategoryColor(article.category)}15`,
                        color: getCategoryColor(article.category),
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(article.date).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => handleEdit(article._id)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => {
                        setSelectedArticle(article);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this article?
            {selectedArticle && (
              <Typography color="error" sx={{ mt: 1 }}>
                "{selectedArticle.title}"
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default ArticleList;